import { useIsInstitutionDetailsUpdate } from '@/features/institution/context/update';
import {
  useProfileInstitutionDetailsAnswers,
  useProfileInstitutionDetailsDraft,
  useProfileInstitutionDetailsDraftMutation,
  useProfileInstitutionDetailsMutation,
  useProfileInstitutionDetailsV2Answers,
  useProfileInstitutionDetailsVersion,
} from '@/hooks/queries/use-profile-institution-details';
import type { InstitutionDetailsLocked, InstitutionDetailsV2OnboardingKey } from '@/services/api/profile';

// The details sections work on the draft inside the update flow and on the live record everywhere else

export const useInstitutionDetailsVersion = () => {
  const isUpdate = useIsInstitutionDetailsUpdate();
  const liveVersion = useProfileInstitutionDetailsVersion();
  const { data: draft, ...draftQuery } = useProfileInstitutionDetailsDraft(isUpdate);

  return isUpdate ? { ...draftQuery, data: draft?.version } : liveVersion;
};

export const useInstitutionDetailsAnswers = () => {
  const isUpdate = useIsInstitutionDetailsUpdate();
  const liveAnswers = useProfileInstitutionDetailsAnswers();
  const { data: draft, ...draftQuery } = useProfileInstitutionDetailsDraft(isUpdate);

  return isUpdate ? { ...draftQuery, data: draft?.answers, isCompleteOnboarding: draft?.isCompleteOnboarding, isCompleteFutures: draft?.isCompleteFutures } : liveAnswers;
};

export const useInstitutionDetailsV2Answers = () => {
  const isUpdate = useIsInstitutionDetailsUpdate();
  const liveV2Answers = useProfileInstitutionDetailsV2Answers();
  const { data: draft, ...draftQuery } = useProfileInstitutionDetailsDraft(isUpdate);

  return isUpdate ? { ...draftQuery, data: draft?.answers } : liveV2Answers;
};

export const useInstitutionDetailsMutation = () => {
  const isUpdate = useIsInstitutionDetailsUpdate();
  const liveMutation = useProfileInstitutionDetailsMutation();
  const draftMutation = useProfileInstitutionDetailsDraftMutation();

  return isUpdate ? draftMutation : liveMutation;
};

const NO_LOCKED_ANSWERS: InstitutionDetailsLocked = {};

const NO_FULLY_LOCKED_KEYS: InstitutionDetailsV2OnboardingKey[] = [];

// Only the update flow locks answers, which it keeps from the live record
export const useInstitutionDetailsLocked = () => {
  const isUpdate = useIsInstitutionDetailsUpdate();
  const { data: draft } = useProfileInstitutionDetailsDraft(isUpdate);

  return {
    locked: (isUpdate && draft?.locked) || NO_LOCKED_ANSWERS,
    fullyLockedKeys: (isUpdate && draft?.fullyLockedKeys) || NO_FULLY_LOCKED_KEYS,
  };
};

// The free-text registered address an older record holds, which the update asks for again as a structured one
export const useInstitutionDetailsPreviousRegisteredAddress = () => {
  const isUpdate = useIsInstitutionDetailsUpdate();
  const { data: liveAnswers } = useProfileInstitutionDetailsAnswers();
  const registeredAddress = liveAnswers?.company?.registeredAddress;

  return isUpdate && typeof registeredAddress === 'string' ? registeredAddress : undefined;
};
